import { Box, useTheme } from "@mui/material";
import {
  getSelfSignSignatureImage,
  getSelfSignInitialsImage,
} from "../ParticipantScreen/ParticipantScreenService";
import CloseIcon from "@mui/icons-material/Close";
import strings from "global/constants/StringConstants";
import { useAppSelector } from "hooks/use-app-select";
import { Coordinates, FileField } from "models/interfaces";
import { useCallback, useEffect, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import { useAppDispatch } from "redux/hooks";
import { RootState } from "utils/store";
import {
  fieldColorFor,
  PLACED_FIELD_HELP_ID,
  getField,
  isAnySignerField,
} from "../SignRequestHelper";
import editorTokens from "../SignRequestEditor.tokens";
import { SIGNER_COLORS } from "utils/SignerColors";
import {
  setLoading,
  deleteFieldOfSelectedFile,
  selectField,
  setSelectedField,
  updateFieldInSignRequest,
  updateFieldOfSelectedFile,
} from "redux/appSlice";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import SignaturePad from "screens/SignRequest/ParticipantScreen/SignaturePad/SignaturePad";
import SavedSignatureDialog from "../ParticipantScreen/components/SavedSignatureDialog";
import { useLocation } from "react-router-dom";
import viewpaths from "global/constants/ViewPathConstants";
import {
  uploadSelfSignInitialsImage,
  uploadSelfSignSignImage,
} from "../SignRequestService";
import AttachmentUploadDialog from "../ParticipantScreen/components/AttachmentUploadDialog";
import AttachmentPreviewDialog from "../ParticipantScreen/components/AttachmentPreviewDialog";
import {
  encodeAttachmentValue,
  setAttachmentPolicy,
  SignerAttachmentItem,
} from "../ParticipantScreen/components/AttachmentPolicy";
import { loadAccountAttachmentConfig } from "helpers/attachmentConfig";
import {
  attachmentFieldNames,
  ensureAttachmentFieldName,
  getHeldAttachments,
  heldAttachmentsElsewhere,
  makeLocalAttachmentItem,
  pruneHeldAttachments,
  setHeldAttachments,
} from "../selfSignAttachmentStore";

const DraggableField = ({
  field,
  onFieldResize,
  onFieldMove,
  setAnchorEl,
  onFieldClick,
  onFieldDoubleClick,
  onFocus,
  popover,
  setCurrentField,
  isMobile,
  onRequestEditSignature,
}: {
  field: FileField;
  onFieldResize: (node: HTMLElement, event: any) => void;
  onFieldMove: (node: HTMLElement, event: any) => void;
  setAnchorEl: Function;
  onFieldClick?: Function;
  onFieldDoubleClick?: Function;
  onFocus?: () => void;
  popover: any;
  setCurrentField: Function;
  isMobile?: boolean;
  onRequestEditSignature?: (fieldId: number, cb: () => void) => void;
}) => {
  const color = fieldColorFor(field.signerIndex);
  const isMobileValue = isMobile;
  const theme = useTheme();
  const m = theme.palette.mobile;

  const $el = useRef(null);
  const rndRef = useRef<any>(null);
  const onFieldClickRef = useRef(onFieldClick);

  const longPressTimer = useRef<any>(null);
  const longPressFired = useRef(false);
  const isTouchHandledRef = useRef(false);
  const isDraggingRef = useRef(false);
  const dragStartCoords = useRef<{ x: number; y: number } | null>(null);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const MOVE_THRESHOLD = 10;

  const isCheckBox =
    field.type === strings.checkbox || field.type === strings.radioButton;
  const isRadioButton = field.type === strings.radioButton;
  const isdateField = field.type === strings.date;
  const isImage =
    field.type === strings.signature || field.type === strings.initials;
  const isText = [
    strings.text,
    strings.email,
    strings.name,
    strings.title,
    strings.companyName,
    strings.dateSigned,
  ].includes(field.type.replace("_", " "));

  const dispatch = useAppDispatch();

  const { selectedField, selectedFile, signRequest } = useAppSelector(
    (state: RootState) => state.app,
  );
  const selectedFileFieldsRef = useRef<any>(null);
  selectedFileFieldsRef.current = selectedFile?.fields;

  const location = useLocation();

  const isSelfSign =
    location.pathname === viewpaths.createSelfSignRequestPath ||
    signRequest?.selfSigned === true;

  const isInitials = field.type === strings.initials;
  const isAttachment = field.type === strings.attachment;

  // Accessible name for the placed field: describes the field type, the signer
  // it is assigned to, and whether it is required, so assistive tech can convey
  // the same information sighted users get from the coloured chip (1.3.1/3.3.2).
  const assignedSigner = signRequest?.signers?.find(
    (s: any) => s.id === field.signerIndex,
  );
  const fieldTypeLabel = (field.type || "form").replace(/_/g, " ").trim();
  const fieldAccessibleName = [
    `${fieldTypeLabel} field`,
    assignedSigner?.name
      ? `for ${assignedSigner.name}`
      : isAnySignerField(field)
        ? "for any signer"
        : "",
    field.required ? "(required)" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const [isSignPadOpen, setIsSignPadOpen] = useState(false);
  const [isAttachDialogOpen, setIsAttachDialogOpen] = useState(false);
  const [attachmentPreview, setAttachmentPreview] =
    useState<SignerAttachmentItem | null>(null);
  const [attachmentItems, setAttachmentItems] = useState<
    SignerAttachmentItem[]
  >([]);
  const [localField, setLocalField] = useState<FileField>(field);
  const [signatureExist, setSignatureExist] = useState<boolean>(false);
  const [savedImage, setSavedImage] = useState<string>("");

  const handleLocalDispatch = useCallback((action: any) => {
    if (
      action.type === "UPDATE_SIGN_IMAGE" ||
      action.type === "UPDATE_INITIALS_IMAGE"
    ) {
      const dataUrl: string = action.signImage ?? action.initialsImage ?? "";
      if (dataUrl) {
        setSavedImage(dataUrl);
      }
    }
  }, []);

  const handleImageFieldClick = useCallback(
    async (currentFields: FileField[]) => {
      dispatch(selectField(field));
      if (isTruthy(field.value)) return;

      // check if any existing field of same type has a value
      const existingField = currentFields?.find(
        (f: FileField) =>
          f.id !== field.id && f.type === field.type && isTruthy(f.value),
      );
      if (existingField?.value) {
        // auto-fill instead of opening pad
        dispatch(
          updateFieldOfSelectedFile({
            id: field.id,
            value: existingField.value,
          }),
        );
        dispatch(
          updateFieldInSignRequest({
            id: field.id,
            value: existingField.value,
          }),
        );

        // currentFields?.forEach((f: FileField) => {
        //   if (f.id === field.id || f.type !== field.type) return;
        //   if (isTruthy(f.value)) {
        //     dispatch(updateFieldOfSelectedFile({ id: f.id, value: existingField.value }));
        //     dispatch(updateFieldInSignRequest({ id: f.id, value: existingField.value }));
        //   }
        // });
      } else {
        // If all fields of this type are blank, check if we have a saved signature/initials image
        try {
          dispatch(setLoading(true));
          if (isInitials) {
            await getSelfSignInitialsImage(
              setSignatureExist,
              handleLocalDispatch,
              setIsSignPadOpen,
            );
          } else {
            await getSelfSignSignatureImage(
              setSignatureExist,
              handleLocalDispatch,
              setIsSignPadOpen,
            );
          }
        } catch (error: any) {
          setIsSignPadOpen(true);
          handleErrorMessage(error);
        } finally {
          dispatch(setLoading(false));
        }
      }
    },
    [field, isInitials, dispatch, handleLocalDispatch],
  );


  useEffect(() => {
    if (!isSelfSign || !isAttachment) return;
    const name = ensureAttachmentFieldName(field.name);
    if (name !== field.name) {
      dispatch(updateFieldOfSelectedFile({ id: field.id, name }));
      dispatch(updateFieldInSignRequest({ id: field.id, name }));
    }
    setAttachmentItems(getHeldAttachments(name));
  }, [isSelfSign, isAttachment, field.id, field.name, dispatch]);

  const handleAttachmentsChange = useCallback(
    (next: SignerAttachmentItem[]) => {
      setAttachmentItems(next);
      setHeldAttachments(field.name, next);
      const value = encodeAttachmentValue(next);
      dispatch(updateFieldOfSelectedFile({ id: field.id, value }));
      dispatch(updateFieldInSignRequest({ id: field.id, value }));
    },
    [dispatch, field.id, field.name],
  );

  const openAttachmentDialog = useCallback(async () => {
    dispatch(selectField(field));
    // Files held for a field the sender has since deleted are still counted against the per-signer
    // limit, so without this a fresh field can open with no slots left. Whole request, not this file:
    // an attachment field on another document of the envelope shares the same allowance.
    pruneHeldAttachments(attachmentFieldNames(signRequest?.files));
    try {
      setAttachmentPolicy(await loadAccountAttachmentConfig());
    } catch {
      // Non-fatal, deliberately silent: the sender is placing a field, not uploading yet.
    }
    setIsAttachDialogOpen(true);
  }, [dispatch, field, signRequest?.files]);

  // Stand-ins for the dialog's two server calls. Nothing to upload to yet, so a chosen file just
  // becomes a row; removing one is the dialog dropping it from its own list.
  const holdAttachment = useCallback(
    async (file: File) => makeLocalAttachmentItem(file),
    [],
  );
  const dropAttachment = useCallback(async () => {}, []);

  useEffect(() => {
    if (!isSignPadOpen) {
      setLocalField(field);
    }
  }, [field, isSignPadOpen]);

  useEffect(() => {
    if (isSelfSign && isImage) {
      onRequestEditSignature?.(field.id, () => setIsSignPadOpen(true));
    }
  }, [isSelfSign, isImage, field.id]);

  useEffect(() => {
    if (!isSelfSign || !isImage) return;

    if (field.autoFilled) return;

    if (isTruthy(field.value)) {
      dispatch(updateFieldOfSelectedFile({ id: field.id, autoFilled: true }));
      dispatch(updateFieldInSignRequest({ id: field.id, autoFilled: true }));
      return;
    }

    const existingField = selectedFile?.fields?.find(
      (f: FileField) =>
        f.id !== field.id &&
        f.type === field.type &&
        f.signerIndex === field.signerIndex &&
        isTruthy(f.value),
    );

    if (existingField?.value) {
      dispatch(
        updateFieldOfSelectedFile({
          id: field.id,
          value: existingField.value,
          autoFilled: true,
        }),
      );
      dispatch(
        updateFieldInSignRequest({
          id: field.id,
          value: existingField.value,
          autoFilled: true,
        }),
      );
    } else {
      dispatch(updateFieldOfSelectedFile({ id: field.id, autoFilled: true }));
      dispatch(updateFieldInSignRequest({ id: field.id, autoFilled: true }));
    }
  }, [
    field.id,
    field.value,
    selectedFile?.fields,
    isSelfSign,
    isImage,
    field.signerIndex,
    dispatch,
  ]);

  const handleSignatureAccepted = useCallback(
    (dataUrl: string) => {
      dispatch(updateFieldOfSelectedFile({ id: field.id, value: dataUrl }));
      dispatch(updateFieldInSignRequest({ id: field.id, value: dataUrl }));

      selectedFile?.fields?.forEach((f: FileField) => {
        if (
          f.id === field.id ||
          f.type !== field.type ||
          f.signerIndex !== field.signerIndex
        )
          return;

        if (isTruthy(f.value)) {
          dispatch(updateFieldOfSelectedFile({ id: f.id, value: dataUrl }));
          dispatch(updateFieldInSignRequest({ id: f.id, value: dataUrl }));
        }
      });

      setIsSignPadOpen(false);
    },
    [dispatch, field.id, field.type, field.signerIndex, selectedFile?.fields],
  );

  const handleSignaturePadDispatch = useCallback(
    (action: any) => {
      if (action.type === "UPDATE_FIELD") {
        setLocalField((prev: FileField) => ({ ...prev, value: action.value }));
      } else if (
        action.type === "UPDATE_SIGN_IMAGE" ||
        action.type === "UPDATE_INITIALS_IMAGE"
      ) {
        const dataUrl: string = action.signImage ?? action.initialsImage ?? "";
        if (dataUrl) handleSignatureAccepted(dataUrl);
      }
    },
    [handleSignatureAccepted],
  );

  const base64ToImageFile = useCallback(
    (base64String: string, fileName: string): Promise<File | null> => {
      const str = base64String.split(",")[0];
      const match = str.match(/^data:(.*?);base64$/);
      const contentType = match ? match[1] : "";
      const byteCharacters = atob(base64String.split(",")[1]);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: contentType });

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.width = 270;
          canvas.height = 130;
          context?.drawImage(img, 0, 0, 270, 130);
          canvas.toBlob((croppedBlob) => {
            if (croppedBlob) {
              resolve(new File([croppedBlob], fileName, { type: contentType }));
            } else {
              resolve(null);
            }
          });
        };
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = URL.createObjectURL(blob);
      });
    },
    [],
  );

  const handleImageUpload = useCallback(
    async (img: string, isInitialsImg: boolean) => {
      try {
        dispatch(setLoading(true));
        const fileName = `${field.type}${Date.now()}.png`;
        const file = await base64ToImageFile(img, fileName);
        const formData = new FormData();
        formData.append("file", file!, file!.name);
        isInitialsImg
          ? await uploadSelfSignInitialsImage(formData)
          : await uploadSelfSignSignImage(formData);
      } catch (error: any) {
        handleErrorMessage(error);
      } finally {
        dispatch(setLoading(false));
      }
    },
    [field.name, base64ToImageFile],
  );

  const [coords, setCoords] = useState<Coordinates>({
    pageX: 0,
    pageY: 0,
    pageH: 0,
    pageW: 0,
  });

  const calculateCoords = useCallback(() => {
    const $page = document.querySelector<HTMLElement>(
      `.react-pdf__Page[data-page-number="${field.page + 1}"]`,
    );

    if (!$page) return;

    const { height, width } = $page.getBoundingClientRect();

    const top = $page.getBoundingClientRect().top + window.scrollY;
    const left = $page.getBoundingClientRect().left + window.scrollX;

    const percentX = field.fieldPageX ?? 0;
    const percentY = field.fieldPageY ?? 0;

    const pageX = (percentX / 100) * width;
    const pageY = (percentY / 100) * height;

    const pageWidth = ((field.fieldPageWidth ?? 0) / 100) * width;
    const pageHeight = ((field.fieldPageHeight ?? 0) / 100) * height;

    // NOTE: intentionally does NOT dispatch(setSelectedField) — selecting a
    // field is a user action (click/drop), not a side effect of recomputing its
    // pixel position. Dispatching here re-rendered every field on each recompute
    // and helped drive the reflow/heap loop.
    setCoords({
      pageX: pageX,
      pageY: pageY,
      pageH: pageHeight,
      pageW: isCheckBox ? pageHeight : pageWidth,
    });

    setCoords((prevCoords) => {
      if (
        prevCoords.pageX !== pageX ||
        prevCoords.pageY !== pageY ||
        prevCoords.pageW !== (isCheckBox ? pageHeight : pageWidth) ||
        prevCoords.pageH !== pageHeight
      ) {
        return {
          pageX,
          pageY,
          pageW: isCheckBox ? pageHeight : pageWidth,
          pageH: pageHeight,
        };
      }
      return prevCoords;
    });
  }, [field]);

  const calculateExitingCoords = useCallback(() => {
    const $page = document.querySelector<HTMLElement>(
      `.react-pdf__Page[data-page-number="${field.page + 1}"]`,
    );

    if (!$page) return;

    const { height, width } = $page.getBoundingClientRect();
    const top = $page.getBoundingClientRect().top + window.scrollY;
    const left = $page.getBoundingClientRect().left + window.scrollX;

    // Use == null (not truthiness) so a legitimate 0 coordinate — a form field
    // flush against the page's left/top edge, common in I-9/W-9 forms — counts
    // as "already positioned". With the old truthiness check those fields kept
    // re-dispatching on every recompute: each dispatch changed the field object,
    // which re-ran this effect (re-reading page layout → forced reflow) and
    // pushed an undo-history snapshot that was never freed, so the JS heap
    // climbed until the tab ran out of memory.
    const fieldNeedsUpdate =
      field.width == null ||
      field.height == null ||
      field.top == null ||
      field.left == null ||
      field.fieldPageX == null ||
      field.fieldPageY == null ||
      field.fieldPageWidth == null ||
      field.fieldPageHeight == null;

    const exitingPageDims = selectedFile?.dimensions?.[field.page];
    if (!exitingPageDims?.x || !exitingPageDims?.y) return;

    const pageX = (field.x / exitingPageDims.x) * width;

    const pageY = (field.y / exitingPageDims.y) * height;
    // (field.y / selectedFile?.dimensions[field.page].y) * height + field.page * height;

    const pageWidth = (field.w / exitingPageDims.x) * width;
    const pageHeight = (field.h / exitingPageDims.y) * height;

    if (fieldNeedsUpdate) {
      const fieldsToUpdate = {
        id: field.id,
        width,
        height,
        top,
        left,
        fieldPageX: (pageX / width) * 100,
        fieldPageY: (pageY / height) * 100,
        fieldPageWidth: (pageWidth / width) * 100,
        fieldPageHeight: (pageHeight / height) * 100,
        color: SIGNER_COLORS[field.signerIndex],
      };
      dispatch(updateFieldOfSelectedFile(fieldsToUpdate));
      dispatch(updateFieldInSignRequest(fieldsToUpdate));
    }

    // See calculateCoords: do not dispatch(setSelectedField) as a side effect
    // of a coordinate recompute.
    setCoords({
      pageX: pageX,
      pageY: pageY,
      pageH: pageHeight,
      pageW: isCheckBox ? pageHeight : pageWidth,
    });

    setCoords((prevCoords) => {
      const newPageW = isCheckBox ? pageHeight : pageWidth;
      if (
        prevCoords.pageX !== pageX ||
        prevCoords.pageY !== pageY ||
        prevCoords.pageW !== newPageW ||
        prevCoords.pageH !== pageHeight
      ) {
        return {
          pageX,
          pageY,
          pageW: newPageW,
          pageH: pageHeight,
        };
      }
      return prevCoords;
    });
  }, [field]);

  useEffect(() => {
    // Once a field has real percentage coords (fieldPageX/Y set and non-zero),
    // always position it from those. The PDF-point (x/y) path is only meant for
    // the FIRST conversion of an existing field — reusing it afterwards makes a
    // moved field snap back to its original x/y on the next recompute.
    const hasPercentCoords =
      field.fieldPageX != null &&
      field.fieldPageX !== 0 &&
      field.fieldPageY != null &&
      field.fieldPageY !== 0;
    const needsExistingCoords =
      !hasPercentCoords && (field.existingField || field.x > 0 || field.y > 0);

    if (needsExistingCoords) {
      if (isTruthy(selectedFile.dimensions)) {
        calculateExitingCoords();
      }
    } else {
      // For newly added fields, render immediately without waiting for dimensions
      calculateCoords();
    }
  }, [field, selectedFile.dimensions]);

  useEffect(() => {
    const onResize = () => {
      const hasPercentCoords =
        field.fieldPageX != null &&
        field.fieldPageX !== 0 &&
        field.fieldPageY != null &&
        field.fieldPageY !== 0;
      const needsExistingCoords =
        !hasPercentCoords &&
        (field.existingField || field.x > 0 || field.y > 0);

      if (needsExistingCoords) {
        if (isTruthy(selectedFile.dimensions)) {
          calculateExitingCoords();
        }
      } else {
        calculateCoords();
      }
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [calculateCoords, calculateExitingCoords, field]);

  useEffect(() => {
    const onClickOutsideOfField = (event: MouseEvent) => {
      const isOutsideOfField =
        $el.current && !event.composedPath().includes($el.current);
      if (isOutsideOfField) {
        setAnchorEl(null);
      }
    };

    document.body.addEventListener("click", onClickOutsideOfField);

    return () => {
      document.body.removeEventListener("click", onClickOutsideOfField);
    };
  }, []);

  useEffect(() => {
    const $page = document.querySelector<HTMLElement>(
      `.react-pdf__Page[data-page-number="${field.page + 1}"]`,
    );

    if (!$page) return;

    const recalc = () => {
      const hasPercentCoords =
        field.fieldPageX != null &&
        field.fieldPageX !== 0 &&
        field.fieldPageY != null &&
        field.fieldPageY !== 0;
      const needsExistingCoords =
        !hasPercentCoords &&
        (field.existingField || field.x > 0 || field.y > 0);

      if (needsExistingCoords) {
        calculateExitingCoords();
      } else {
        calculateCoords();
      }
    };

    // Recompute coordinates only when the PAGE actually changes size (its canvas
    // mounting late, responsive width changes, sidebar collapse). The previous
    // MutationObserver watched attributes/childList/subtree, so it fired on every
    // react-pdf canvas paint — far more often than needed — and each call reads
    // page layout (getBoundingClientRect), which profiling showed to be the #1
    // forced-reflow source. ResizeObserver fires only on genuine size changes.
    if (typeof ResizeObserver === "undefined") return;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(recalc);
    });
    observer.observe($page);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [field]);

  useEffect(() => {
    onFieldClickRef.current = onFieldClick;
  }, [onFieldClick]);

  useEffect(() => {
    if (!isMobileValue) return;

    // Use RAF + timeout to ensure Rnd is fully mounted
    let cleanup: (() => void) | null = null;

    const attachListeners = () => {
      const rndDomNode = rndRef.current?.resizableElement?.current;
      if (!rndDomNode) {
        // Retry after next paint if not ready
        requestAnimationFrame(attachListeners);
        return;
      }

      const isDeleteTarget = (e: TouchEvent) => {
        const t = e.target as HTMLElement | null;
        return !!t?.closest?.("[data-delete-field]");
      };

      const handleTouchStart = (e: TouchEvent) => {
        // Let the red × delete button handle its own taps.
        if (isDeleteTarget(e)) return;
        longPressFired.current = false;
        isTouchHandledRef.current = false;

        const touch = e.touches[0];
        touchStartPos.current = { x: touch.clientX, y: touch.clientY };

        if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }

        // Mobile intentionally does NOT open the desktop context menu
        // (Properties / Duplicate / Cut / Copy / Change signer / Delete) on
        // long-press: field editing on mobile is driven by the field-options
        // bar + Type sheet, and deletion by the field's × button.
      };

      const handleTouchMove = (e: TouchEvent) => {
        if (!touchStartPos.current) return;
        const touch = e.touches[0];
        const dx = Math.abs(touch.clientX - touchStartPos.current.x);
        const dy = Math.abs(touch.clientY - touchStartPos.current.y);
        if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) {
          if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
          }
          touchStartPos.current = null;
        }
      };

      const handleTouchEnd = (e: TouchEvent) => {
        // Don't intercept taps on the delete button (it deletes the field).
        if (isDeleteTarget(e)) return;
        if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }

        if (longPressFired.current) return;
        if (!touchStartPos.current) return;
        if (isTouchHandledRef.current) return;

        isTouchHandledRef.current = true;
        e.stopPropagation();
        e.preventDefault();

        if (isSelfSign && isImage) {
          handleImageFieldClick(selectedFileFieldsRef.current || []);
          requestAnimationFrame(() => {
            isTouchHandledRef.current = false;
          });
          return;
        }

        if (isSelfSign && isAttachment) {
          openAttachmentDialog();
          requestAnimationFrame(() => {
            isTouchHandledRef.current = false;
          });
          return;
        }

        onFieldClickRef.current?.(e as any);
        if (isText) {
          requestAnimationFrame(() => {
            const textarea =
              rndRef.current?.resizableElement?.current?.querySelector(
                "textarea",
              );
            if (textarea) {
              textarea.focus();
              const len = textarea.value.length;
              textarea.setSelectionRange(len, len);
            }
          });
        }
        if (isCheckBox) {
          requestAnimationFrame(() => {
            const checkboxBox =
              rndRef.current?.resizableElement?.current?.querySelector(
                ".checkbox-field",
              );
            if (checkboxBox) {
              (checkboxBox as HTMLElement).click(); // ← trigger the onClick on CheckBoxField's Box
            }
          });
        }
        requestAnimationFrame(() => {
          isTouchHandledRef.current = false;
        });
      };

      //  capture: true — fires BEFORE Rnd's bubble-phase listeners
      rndDomNode.addEventListener("touchstart", handleTouchStart, {
        passive: true,
        capture: true,
      });
      rndDomNode.addEventListener("touchmove", handleTouchMove, {
        passive: true,
        capture: true,
      });
      rndDomNode.addEventListener("touchend", handleTouchEnd, {
        passive: false,
        capture: true,
      });

      cleanup = () => {
        rndDomNode.removeEventListener("touchstart", handleTouchStart, {
          capture: true,
        });
        rndDomNode.removeEventListener("touchmove", handleTouchMove, {
          capture: true,
        });
        rndDomNode.removeEventListener("touchend", handleTouchEnd, {
          capture: true,
        });
      };
    };

    requestAnimationFrame(attachListeners);

    return () => {
      cleanup?.();
    };
  }, [isMobileValue, field]);

  return (
    <Rnd
      ref={rndRef}
      data-field-id={field.id}
      role="group"
      aria-label={fieldAccessibleName}
      // Keyboard instructions, rendered once by DocumentViewer (WCAG 3.3.2).
      aria-describedby={isMobileValue ? undefined : PLACED_FIELD_HELP_ID}
      // Keyboard access (WCAG 2.1.1): the drag-positioned field is otherwise
      // only selectable by mouse. Make it a tab stop that selects on focus, so
      // keyboard users can reach it and then use the editor's existing
      // shortcuts (Delete / Ctrl+C·X·V), arrow-key nudging, the properties
      // panel, and — via Enter — the same context menu as right-click.
      tabIndex={0}
      onFocus={() => onFocus?.()}
      onKeyDown={(e: any) => {
        // Only when focus is on the field wrapper itself, never when it
        // bubbles up from an inner text input (so typing is unaffected).
        if (e.target !== e.currentTarget) return;
        if (isMobileValue) return;
        if (e.key === "Enter") {
          e.preventDefault();
          onFocus?.();
          popover.handleOpen();
          setAnchorEl(e.currentTarget as HTMLElement);
          setCurrentField(field);
        }
      }}
      cancel=".no-drag"
      resizeHandleStyles={{
        bottom: { height: "3px", cursor: "ns-resize" },
        top: { height: "3px", cursor: "ns-resize" },
        left: { width: "3px", cursor: "ew-resize" },
        right: { width: "3px", cursor: "ew-resize" },
        topLeft: isMobile
          ? {
              width: "8px",
              height: "8px",
              position: "absolute",
              top: "-8px",
              left: "-8px",
              cursor: "nwse-resize",
              borderRadius: "50%",
              // Purple corner dots hidden for now.
              backgroundColor: "transparent",
            }
          : {
              width: isText ? "1px" : "6px",
              height: isText ? "1px" : "6px",
              position: "absolute",
              top: isText ? "-3px" : "-1px",
              left: isText ? "-3px" : "-1px",
              cursor: "nwse-resize",
              borderRadius: "50%",
              ...(isText ? { border: "2px solid " + color } : {}),
            },
        topRight: isMobile
          ? {
              width: "8px",
              height: "8px",
              position: "absolute",
              top: "-8px",
              right: "-8px",
              cursor: "nesw-resize",
              borderRadius: "50%",
              // Purple corner dots hidden for now.
              backgroundColor: "transparent",
            }
          : {
              width: isText ? "1px" : "6px",
              height: isText ? "1px" : "6px",
              position: "absolute",
              top: isText ? "-3px" : "-1px",
              right: isText ? "-3px" : "-1px",
              cursor: "nesw-resize",
              ...(isText ? { border: "2px solid " + color } : {}),
              borderRadius: "50%",
            },
        bottomLeft: isMobile
          ? {
              width: "8px",
              height: "8px",
              position: "absolute",
              bottom: "-8px",
              left: "-8px",
              cursor: "nesw-resize",
              borderRadius: "50%",
              // Purple corner dots hidden for now.
              backgroundColor: "transparent",
            }
          : {
              width: isText ? "1px" : "6px",
              height: isText ? "1px" : "6px",
              position: "absolute",
              bottom: isText ? "-3px" : "-1px",
              left: isText ? "-3px" : "-1px",
              cursor: "nesw-resize",
              ...(isText ? { border: "2px solid " + color } : {}),
              borderRadius: "50%",
            },
        bottomRight: isMobile
          ? {
              width: "8px",
              height: "8px",
              position: "absolute",
              bottom: "-8px",
              right: "-8px",
              cursor: "nwse-resize",
              borderRadius: "50%",
              // Purple corner dots hidden for now.
              backgroundColor: "transparent",
            }
          : {
              width: isImage
                ? "6px"
                : isCheckBox || isdateField
                  ? "4px"
                  : "1px",
              height: isImage
                ? "6px"
                : isCheckBox || isdateField
                  ? "4px"
                  : "1px",
              position: "absolute",
              bottom: isText
                ? "-3px"
                : isCheckBox || isdateField
                  ? "-5px"
                  : "-1px",
              right: isText
                ? "-3px"
                : isCheckBox || isdateField
                  ? "-5px"
                  : "-1px",
              cursor: "nwse-resize",
              backgroundColor: isCheckBox ? color : "none",
              ...(isImage ? {} : { border: "2px solid " + color }),
              borderRadius: "50%",
            },
      }}
      resize
      lockAspectRatio={isCheckBox || isImage}
      key={field.id}
      style={{
        // The placed chip wears the design's dashed 1.5px outline over an 8%
        // tint of its recipient colour (`Sign Request Editor.dc.html`), and
        // gains a solid 2px outline while selected.
        backgroundColor: isText
          ? "transparent"
          : isSelfSign && isImage && isTruthy(field.value)
            ? "transparent"
            : `${color}${editorTokens.placedField.tintHex}`,
        position: "absolute",
        border: isMobileValue
          ? `1px solid ${color}`
          : `${editorTokens.placedField.borderWidth} dashed ${color}`,
        outline:
          !isMobileValue && selectedField.id === field.id
            ? `2px solid ${color}`
            : "none",
        cursor: "pointer",
        zIndex: 20,
        borderRadius: isMobileValue
          ? isImage
            ? "8px"
            : "0px"
          : editorTokens.placedField.radius,

        WebkitUserSelect: "auto",
        userSelect: "auto",
        WebkitTouchCallout: "default",

        // required for drag to work properly
        touchAction: "none",
      }}
      // default={{
      //   x: coords.pageX,
      //   y: coords.pageY,
      //   width: coords.pageW,
      //   height: coords.pageH,
      // }}
      position={{
        x: coords.pageX,
        y: coords.pageY,
      }}
      size={{
        width: coords.pageW,
        height: coords.pageH,
      }}
      bounds={`.react-pdf__Page[data-page-number="${field.page + 1}"]`}
      enableResizing={{
        top: isText,
        right: isText,
        bottom: isText,
        left: isText,
        topRight: !isCheckBox,
        bottomRight: true,
        bottomLeft: !isCheckBox,
        topLeft: !isCheckBox,
      }}
      onClick={(e: any) => {
        if (isMobileValue) {
          e.stopPropagation();
          return;
        }
        if (isDraggingRef.current) {
          e.stopPropagation();
          return;
        }
        onFieldClick?.(e);
      }}
      onResizeStop={(_e: any, _d: any, ref: any) => {
        onFieldResize?.(ref, _e);
      }}
      onDragStop={(_e: any, d: any) => {
        if (dragStartCoords.current && d) {
          const dx = Math.abs(d.x - dragStartCoords.current.x);
          const dy = Math.abs(d.y - dragStartCoords.current.y);
          if (dx > 5 || dy > 5) {
            isDraggingRef.current = true;
            setTimeout(() => {
              isDraggingRef.current = false;
            }, 100);
          } else {
            isDraggingRef.current = false;
          }
        } else {
          isDraggingRef.current = false;
        }
        onFieldMove?.(d.node, _e);
      }}
      onDragStart={(_e: any, d: any) => {
        dragStartCoords.current = d ? { x: d.x, y: d.y } : null;
      }}
    >
      <>
        <Box
          display="flex"
          justifyContent="center"
          alignItems={"center"}
          sx={{
            width:
              isCheckBox && isMobileValue ? coords.pageW - 2 : coords.pageW - 4,
            height:
              isCheckBox && isMobileValue ? coords.pageH - 2 : coords.pageH - 4,
            minHeight: isCheckBox && isMobileValue ? undefined : "12px",
            padding: "0px",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            // Radio buttons render like desktop (no clipping) so the "+" button
            // that adds another option to the group stays visible below the field.
            ...(isMobileValue &&
              !isRadioButton && {
                overflow: "hidden",
                "& *": {
                  // touchAction: "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  fontSize:
                    !isCheckBox &&
                    `${Math.max(Math.min(coords.pageH * 0.45, coords.pageW * 0.15), 6)}px !important`,
                  padding: "0px",
                },
              }),
            ...(isMobileValue && isRadioButton && { overflow: "visible" }),
          }}
          onClick={(e: any) => {
            if (isDraggingRef.current) {
              e.stopPropagation();
              return;
            }
            if (isSelfSign && isImage) {
              e.stopPropagation();
              handleImageFieldClick(selectedFile?.fields || []);
              return;
            }
            if (isSelfSign && isAttachment) {
              e.stopPropagation();
              openAttachmentDialog();
              return;
            }
            onFocus?.();
          }}
          onDoubleClick={(e: any) => {
            if (isSelfSign && isImage) {
              dispatch(selectField(field));
              e.stopPropagation();
              return;
            }
            if (onFieldDoubleClick) {
              onFieldDoubleClick(e, field);
            }
          }}
          onContextMenu={(e: any) => {
            if (isMobileValue) return;
            popover.handleOpen();
            setAnchorEl(e.currentTarget);
            setCurrentField(field);
            e.preventDefault();
          }}
          ref={popover.anchorRef}
        >
          {/* {getField(selectedFile, field, coords, dispatch, isMobileValue)} */}
          {isSelfSign && isImage && isTruthy(field.value) ? (
            <img
              src={field.value}
              alt={isInitials ? "Initials" : "Signature"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                pointerEvents: "none",
              }}
            />
          ) : (
            getField(selectedFile, field, coords, dispatch, isMobileValue)
          )}
        </Box>
        {isSelfSign && isImage && isSignPadOpen && (
          <SignaturePad
            dispatch={handleSignaturePadDispatch}
            selectedFile={selectedFile}
            field={localField}
            setField={setLocalField}
            isOpen={isSignPadOpen}
            setIsOpen={setIsSignPadOpen}
            handleImageUpload={handleImageUpload}
            width={isMobileValue ? 310 : 500}
            height={isMobileValue ? 390 : 270}
            isInitialsPad={isInitials}
            currentFieldOnFocus={0}
            moveToFieldLocation={() => {}}
            isSelfSign={true}
          />
        )}
        {isSelfSign && isImage && signatureExist && (
          <SavedSignatureDialog
            open={signatureExist}
            image={savedImage}
            isInitialsPad={isInitials}
            enabled
            onClose={() => {
              setSavedImage("");
              setSignatureExist(false);
            }}
            onAccept={() => {
              setSignatureExist(false);
              if (savedImage) {
                handleSignatureAccepted(savedImage);
              }
            }}
            onEdit={() => {
              setSavedImage("");
              setSignatureExist(false);
              setIsSignPadOpen(true);
            }}
          />
        )}
        {isSelfSign && isAttachment && isAttachDialogOpen && (
          <AttachmentUploadDialog
            open={isAttachDialogOpen}
            onClose={() => setIsAttachDialogOpen(false)}
            onConfirm={handleAttachmentsChange}
            onPreview={setAttachmentPreview}
            initialItems={attachmentItems}
            usedElsewhere={heldAttachmentsElsewhere(field.name)}
            // No envelope, so no token. uploadFile/removeFile below keep the dialog off the network.
            requestToken=""
            fieldName={field.name}
            fieldLabel={field.toolTip || field.label || undefined}
            required={field.required}
            uploadFile={holdAttachment}
            removeFile={dropAttachment}
          />
        )}
        {isSelfSign && isAttachment && (
          // Every held file still has its File, so this previews straight from memory and never needs
          // the token the signer's copy of this dialog uses to fetch a stored file.
          <AttachmentPreviewDialog
            open={!!attachmentPreview}
            item={attachmentPreview}
            onClose={() => setAttachmentPreview(null)}
          />
        )}
        {isMobileValue && (
          <Box
            className="no-drag"
            data-delete-field="true"
            role="button"
            tabIndex={0}
            aria-label={`Delete ${fieldTypeLabel} field`}
            onClick={(e: any) => {
              e.stopPropagation();
              dispatch(deleteFieldOfSelectedFile(field));
            }}
            onKeyDown={(e: any) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                dispatch(deleteFieldOfSelectedFile(field));
              }
            }}
            onTouchEnd={(e: any) => {
              e.stopPropagation();
              e.preventDefault();
              dispatch(deleteFieldOfSelectedFile(field));
            }}
            sx={{
              position: "absolute",
              top: "-9px",
              right: "-9px",
              width: 18,
              height: 18,
              borderRadius: "50%",
              bgcolor: m.red[500],
              color: m.surface.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 30,
              cursor: "pointer",
            }}
          >
            <CloseIcon sx={{ fontSize: 12 }} />
          </Box>
        )}
      </>
    </Rnd>
  );
};

export default DraggableField;
